import withLifecycle from '@hocs/with-lifecycle';
import { prop, contains } from 'ramda';
import { withRouter } from 'react-router';
import { branch, compose, setDisplayName, withHandlers, withProps } from 'recompose';
import { Layout } from '../../constants/Layout';
import { MainSource } from '../../constants/Source';
import { getLocale } from '../../utils/env/getLocale';
import { withAppLoadedAnalytics } from '../../utils/hoc/bi/withAppLoadedAnalytics';
import { withTrackAddMediaSideBarActionsAnalytics } from '../../utils/hoc/bi/withTrackAddMediaSideBarActionsAnalytics';
import { withShutterstock, withUnsplash } from '../../utils/hoc/extended-sources';
import { withMediaHome } from '../../utils/hoc/extended-sources/withMediaHome';
import { ifNonEmptyRoute } from '../../utils/hoc/ifNonEmptyRoute';
import { onPropChange } from '../../utils/hoc/onPropChange';
import { withFolderPath } from '../../utils/hoc/withFolderPath';
import { withHintTooltip } from '../../utils/hoc/withHintTooltip';
import { withItemsLoader } from '../../utils/hoc/withItemsLoader';
import { withLayoutAndSort } from '../../utils/hoc/withLayoutAndSort';
import { withLoadingErrorCatcher } from '../../utils/hoc/withLoadingErrorCatcher';
import { withMediaManagerSDK } from '../../utils/hoc/withMediaManagerSDK';
import { withMediaTypes } from '../../utils/hoc/withMediaTypes';
import { withoutRouter } from '../../utils/hoc/withoutRouter';
import { withPublicMedia } from '../../utils/hoc/withPublicMedia';
import { withRecentSearches } from '../../utils/hooks/useRecentSearches';
import {
  decodePublicMediaFolderId,
  encodePublicMediaFolderId,
  getCategoriesData,
} from '../../utils/hoc/withPublicMedia/utils';
import { withSources } from '../../utils/hoc/withSources';
import { isBoardId, isRootBoardId } from '../../utils/media-manager-sdk/boards';
import {
  isAllSource,
  isBoards as isBoardsSource,
  isSiteFiles as isSiteFilesSource,
} from '../../utils/media-manager-sdk/sources';
import { RouteBuilder, RouteMatcher } from '../../utils/RouteHelpers';
import { IInner, IOuter } from './Root.types';
import { withMediaManagerApi } from '../../utils/hoc/withMediaManagerApi';

export const enhance = compose<IInner, IOuter>(
  setDisplayName('RootEnhancer'),
  withSources,
  withHintTooltip('mediaTypes'),
  withHandlers({
    navigateToEnterpriseTab:
      ({ navigateTo }) =>
      (tab: string, folderId?: string) =>
        navigateTo(RouteBuilder.EnterpriseTab(tab, folderId)),
    navigateToFolder:
      ({ navigateTo, searchQuery }) =>
      (folderId: string | undefined, source: MainSource, newSearchQuery: string = searchQuery, fileId: string = '') => {
        if (newSearchQuery) {
          navigateTo(RouteBuilder.Search(source, newSearchQuery, folderId));
        } else {
          navigateTo(RouteBuilder.Source(source, folderId, fileId));
        }
      },
  }),
  withFolderPath(
    ({ isSiteFiles, isSearch, source, isUnsplash, isFavorites, isHome, isPublicMediaLike }) => ({
      validateFolderIdIfCanGetFolderInfo: (folderId, props) => {
        /*
          after 'empty' event is received, folderToLoad can contain encoded public folder id with dots inside,
          but source will be 'private' causing broken requests to privateSite.folder.get
        */
        const mixedSources = contains('.', folderId) && isSiteFilesSource(props.source);
        /*
          when navigating from public media to boards, folderPath state
          may still contain old public folder ID while source has already changed to 'boards',
          causing broken requests to /v3/boards/{publicFolderId}
        */
        const boardsWithInvalidId =
          isBoardsSource(props.source) && folderId && !isRootBoardId(folderId) && !isBoardId(folderId);
        return { isValid: !mixedSources && !boardsWithInvalidId, fallbackFolder: null };
      },
      isPathFolderValid: !isSiteFiles,
      shouldLoadFolders:
        !isPublicMediaLike &&
        !isHome &&
        !isFavorites &&
        !isUnsplash &&
        (!isSearch || (isSearch && !isAllSource(source))),
    }),
    ['source'],
  ),
  withMediaManagerSDK(),
  withLayoutAndSort(),
  withRecentSearches,
  withMediaTypes,
  withTrackAddMediaSideBarActionsAnalytics,
  onPropChange('isTrash', ({ isTrash, selectAllMediaTypes, restorePreviousMediaTypes }) =>
    isTrash ? selectAllMediaTypes() : restorePreviousMediaTypes(),
  ),
  branch(
    ({ isPublicMediaLike }) => isPublicMediaLike,
    compose(
      withPublicMedia,
      withProps(({ folderId, categories, enterpriseTab }) => {
        const decodedFolderId = decodePublicMediaFolderId(folderId);

        // Due to backend limitations we can't search in all categories of Enterprise media
        // so we restrict to the selected category only
        // FIXME: remove once we completed p13n
        const categoriesForLoader =
          enterpriseTab &&
          decodedFolderId.categoryId &&
          decodedFolderId.subCategoryId &&
          categories[decodedFolderId.categoryId]
            ? [{ id: decodedFolderId.subCategoryId, mediaType: categories[decodedFolderId.categoryId].mediaType }]
            : getCategoriesData(folderId, categories);

        return {
          publicMediaFolderToOpen: encodePublicMediaFolderId({
            categoryId: decodedFolderId.categoryId,
            subCategoryId: decodedFolderId.subCategoryId,
          }),
          categories: categoriesForLoader,
        };
      }),
    ),
  ),
  branch(prop('isShutterstock'), withShutterstock),
  branch(prop('isUnsplash'), withUnsplash),
  branch(prop('isHome'), withMediaHome),
  branch(prop('isRecommended'), withLayoutAndSort({ layouts: [Layout.Gallery] })),
  branch(prop('isSearch'), withLayoutAndSort({ layouts: [Layout.Gallery] })),
  // withMediaManagerApi should be the last one before withItemsLoader so that it will take into account any overrides
  // of the SDK methods that might happen above in the enhancers chain via branch() or smth else
  // for example withPublicMedia overrides mediManagerSDK
  withMediaManagerApi,
  withProps(({ source, mediaManagerApi, privateItemInfoMP }) =>
    source === 'private' ? { privateItemInfoMP: mediaManagerApi.privateItemInfo } : {},
  ),
  ifNonEmptyRoute(
    withItemsLoader(
      ({ searchQuery, searchCacheKiller, recommendedCategories, categories }) => ({
        loaderOptions: {
          language: getLocale(),
          searchQuery,
          searchCacheKiller,
          recommendedCategories,
          categories,
        },
      }),
      ['searchQuery', 'searchCacheKiller', 'recommendedCategories', 'categories'],
    ),
  ),
  withRouter,
  withLoadingErrorCatcher,
  withProps(({ location, isLoading, isNotAuthorized, isUnknownLoadingError }) => ({
    isLoading: isLoading && !isNotAuthorized && !isUnknownLoadingError,
    isEmptyRoute: RouteMatcher.isEmpty(location.pathname),
  })),
  withoutRouter,
  withAppLoadedAnalytics,
  withLifecycle({
    onDidMount({ isEmptyRoute, trackMediaManagerLoaded }) {
      if (isEmptyRoute) {
        trackMediaManagerLoaded();
      }
    },
  }),
);
